import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, ModalHeader, Spinner } from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as moment from "moment";

import { useRecordPermissions } from "./useRecordPermissions";
import { RecordKindConfig, RecordStatus, RecordSummary } from "./types";

/**
 * Master-detail editor for one kind of record on a profile (a contractor or
 * a person).
 *
 * The list on the left holds every record; picking one opens it on the right,
 * where it is saved, discarded or removed on its own. Saving one record never
 * touches the others, so each keeps its own "last updated" date.
 */

type Selection = { id: number } | "new" | null;
type PendingAction = { type: "select"; target: Selection } | { type: "close" } | null;

const keyOf = (selection: Selection) =>
  selection === "new" ? "new" : selection ? `record-${selection.id}` : "none";

const sameSelection = (a: Selection, b: Selection) => keyOf(a) === keyOf(b);

const formatDay = (value: any) => (value ? moment(value).format("D MMM YYYY") : "");

/** A failed save's message, with any field-level errors placed on the form. */
const readSaveError = (error: any, form: any, fallback: string) => {
  const apiError = error?.response?.data?.error;
  const details = apiError?.details?.errors;
  if (Array.isArray(details)) {
    details.forEach((detail: any) => {
      const field = detail?.path?.[0];
      if (field) form.setFieldError(field, detail.message);
    });
  }
  return apiError?.message || fallback;
};

const StatusChip = ({ status }: { status: RecordStatus }) => (
  <span className={`badge bg-${status.tone}-subtle text-ink-${status.tone}`}>{status.label}</span>
);

const Avatar = ({ avatar }: { avatar: RecordSummary["avatar"] }) => (
  <span className={`record-manager__avatar is-${avatar.tone}`} aria-hidden="true">
    {avatar.icon ? <i className={avatar.icon}></i> : avatar.initials}
  </span>
);

// ── detail pane ──────────────────────────────────────────────────────────────

type DetailProps = {
  config: RecordKindConfig;
  owner: any;
  record: any | null;
  canSave: boolean;
  canRemove: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: (savedId: number | null, created: boolean) => Promise<void>;
  onRemoved: () => Promise<void>;
  onCancelNew: () => void;
  onBack: () => void;
};

const RecordDetail = ({
  config,
  owner,
  record,
  canSave,
  canRemove,
  onDirtyChange,
  onSaved,
  onRemoved,
  onCancelNew,
  onBack,
}: DetailProps) => {
  const isNew = !record;
  const summary = record ? config.summary(record) : null;
  const readOnly = !canSave;
  const [saveError, setSaveError] = useState("");
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const initialValues = useMemo(
    () => (record ? config.toValues(record) : config.emptyValues),
    [config, record]
  );

  const form = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: config.schema,
    onSubmit: async (values) => {
      setSaveError("");
      const payload = config.toPayload(values);
      const store = config.store;
      try {
        let savedId: number | null = record?.id ?? null;
        if (isNew) savedId = await store.add(owner, payload);
        else await store.edit(owner, record, payload);
        toast.success(
          isNew
            ? `${capitalise(config.noun)} added`
            : `${capitalise(config.noun)} saved`,
          { autoClose: 2000 }
        );
        onDirtyChange(false);
        await onSaved(savedId, isNew);
      } catch (error: any) {
        setSaveError(readSaveError(error, form, `The ${config.noun} could not be saved.`));
      }
    },
  });

  useEffect(() => {
    onDirtyChange(form.dirty);
  }, [form.dirty, onDirtyChange]);

  // A new record starts at its first field; an existing one at its heading,
  // so screen readers announce which record is open.
  useEffect(() => {
    if (isNew) {
      const first = document.querySelector<HTMLElement>(
        ".record-manager__detail-body input:not([type=hidden]), .record-manager__detail-body textarea"
      );
      first?.focus();
    } else {
      titleRef.current?.focus();
    }
  }, [isNew]);

  const remove = async () => {
    setRemoving(true);
    setSaveError("");
    try {
      await config.store.remove(owner, record);
      toast.success(`${capitalise(config.noun)} removed`, { autoClose: 2000 });
      onDirtyChange(false);
      await onRemoved();
    } catch (error: any) {
      setSaveError(readSaveError(error, form, `The ${config.noun} could not be removed.`));
      setConfirmingRemove(false);
    } finally {
      setRemoving(false);
    }
  };

  const link = record && config.profileLink?.(record);
  const status = record ? config.status?.(record) : null;
  const timestampLine = record
    ? [
        record.createdAt && `Added ${formatDay(record.createdAt)}`,
        record.updatedAt &&
          record.updatedAt !== record.createdAt &&
          `updated ${formatDay(record.updatedAt)}`,
      ]
        .filter(Boolean)
        .join(", ")
    : "";
  const updatedLine = timestampLine || (record && config.dateLine?.(record)) || "";

  return (
    <form
      className="record-manager__detail"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) form.handleSubmit();
      }}
    >
      <div className="record-manager__detail-head">
        <button
          type="button"
          className="btn btn-link p-0 mb-2 record-manager__back"
          onClick={onBack}
        >
          <i className="ri-arrow-left-line me-1"></i>All {config.noun}s
        </button>
        <div className="d-flex align-items-start gap-3">
          {summary ? <Avatar avatar={summary.avatar} /> : null}
          <div className="flex-grow-1 min-w-0">
            <h5
              ref={titleRef}
              tabIndex={-1}
              className="record-manager__detail-title mb-1"
            >
              {summary ? summary.title : `New ${config.noun}`}
              {status ? (
                <span className="ms-2 align-middle">
                  <StatusChip status={status} />
                </span>
              ) : null}
            </h5>
            {record && (updatedLine || link) ? (
              <div className="record-manager__provenance">
                {updatedLine ? (
                  <i className="ri-time-line me-1" aria-hidden="true"></i>
                ) : null}
                {updatedLine}
                {link ? (
                  <>
                    {updatedLine ? (
                      <span className="mx-2" aria-hidden="true">
                        ·
                      </span>
                    ) : null}
                    <Link to={link.to} target="_blank" rel="noopener noreferrer">
                      {link.label}
                      <i className="ri-external-link-line ms-1" aria-hidden="true"></i>
                      <span className="visually-hidden"> (opens in a new tab)</span>
                    </Link>
                  </>
                ) : null}
              </div>
            ) : !record ? (
              <div className="record-manager__provenance">
                Fields marked <span className="text-ink-danger">*</span> are required.
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="record-manager__detail-body">
        {readOnly ? (
          <div className="record-manager__notice is-info mb-3" role="note">
            <i className="ri-lock-line" aria-hidden="true"></i>
            <span>Your role can view this {config.noun} but not change it.</span>
          </div>
        ) : null}
        {saveError ? (
          <div className="record-manager__notice is-danger mb-3" role="alert">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
            <span>{saveError}</span>
          </div>
        ) : null}
        <config.Fields form={form} readOnly={readOnly} ownerId={owner?.id} />
      </div>

      <div className="record-manager__detail-foot">
        {confirmingRemove ? (
          <div className="record-manager__confirm" role="alertdialog" aria-labelledby="record-remove-title">
            <div className="record-manager__confirm-text">
              <div id="record-remove-title" className="fw-semibold">
                Remove this {config.noun}?
              </div>
              <div className="record-manager__provenance">{config.removeNote(record)}</div>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setConfirmingRemove(false)}
                disabled={removing}
              >
                Keep it
              </button>
              <button type="button" className="btn btn-danger" onClick={remove} disabled={removing}>
                {removing ? <Spinner size="sm" className="me-2" /> : null}
                Remove {config.noun}
              </button>
            </div>
          </div>
        ) : (
          <>
            {!isNew && canRemove ? (
              <button
                type="button"
                className="btn btn-soft-danger"
                onClick={() => setConfirmingRemove(true)}
                disabled={form.isSubmitting}
              >
                <i className="ri-delete-bin-line me-1" aria-hidden="true"></i>
                Remove
              </button>
            ) : null}
            <div className="ms-auto d-flex gap-2">
              {isNew ? (
                <button type="button" className="btn btn-light" onClick={onCancelNew}>
                  Cancel
                </button>
              ) : form.dirty ? (
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    form.resetForm();
                    setSaveError("");
                  }}
                  disabled={form.isSubmitting}
                >
                  Discard changes
                </button>
              ) : null}
              {canSave ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={form.isSubmitting || (!isNew && !form.dirty)}
                >
                  {form.isSubmitting ? <Spinner size="sm" className="me-2" /> : null}
                  {isNew ? `Add ${config.noun}` : "Save changes"}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </form>
  );
};

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// ── modal ────────────────────────────────────────────────────────────────────

type ModalProps = {
  isOpen: boolean;
  toggle: () => void;
  owner: any;
  /** How the profile is named in empty states. */
  ownerName?: string;
  config: RecordKindConfig;
  /** Reloads the profile so the list and the page show the change. */
  refresh: () => Promise<any>;
};

const RecordManagerModal = ({
  isOpen,
  toggle,
  owner,
  ownerName,
  config,
  refresh,
}: ModalProps) => {
  const permissions = useRecordPermissions(config);
  const records = useMemo(() => config.records(owner), [config, owner]);

  const [selection, setSelection] = useState<Selection>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [query, setQuery] = useState("");
  const [showDetailOnPhone, setShowDetailOnPhone] = useState(false);
  const dirtyRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  // Ids present before an add whose new id the store could not report; the
  // record that appears after the refresh is the new one.
  const [idsBeforeAdd, setIdsBeforeAdd] = useState<Set<any> | null>(null);

  useEffect(() => {
    if (!idsBeforeAdd) return;
    const added = records.find((record: any) => !idsBeforeAdd.has(record.id));
    if (added) {
      setSelection({ id: added.id });
      setIdsBeforeAdd(null);
    }
  }, [idsBeforeAdd, records]);

  const onDirtyChange = useCallback((next: boolean) => {
    dirtyRef.current = next;
    setDirty(next);
  }, []);

  // Open on the first record, so there is always something to read.
  useEffect(() => {
    if (!isOpen) return;
    setPending(null);
    setQuery("");
    setShowDetailOnPhone(false);
    onDirtyChange(false);
    setSelection(records.length ? { id: records[0].id } : null);
    // Only when the modal opens; later changes to `records` keep the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedRecord =
    selection && selection !== "new"
      ? records.find((record: any) => record.id === selection.id) || null
      : null;

  const apply = (action: NonNullable<PendingAction>) => {
    setPending(null);
    onDirtyChange(false);
    if (action.type === "close") {
      toggle();
      return;
    }
    setSelection(action.target);
    setShowDetailOnPhone(!!action.target);
  };

  const request = (action: NonNullable<PendingAction>) => {
    if (action.type === "select" && sameSelection(action.target, selection)) {
      setShowDetailOnPhone(!!action.target);
      return;
    }
    if (dirtyRef.current) {
      setPending(action);
      return;
    }
    apply(action);
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return records;
    return records.filter((record: any) => {
      const summary = config.summary(record);
      return `${summary.title} ${summary.meta || ""}`.toLowerCase().includes(needle);
    });
  }, [config, query, records]);

  const pendingName =
    selectedRecord ? config.summary(selectedRecord).title : `the new ${config.noun}`;
  const footnote = config.footnote?.(owner);
  const canSaveSelected = selection === "new" ? permissions.canAdd : permissions.canEdit;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => request({ type: "close" })}
      size="xl"
      centered
      className="record-manager-modal"
      labelledBy="record-manager-title"
    >
      <ModalHeader
        className="p-3 border-bottom"
        toggle={() => request({ type: "close" })}
        tag="div"
      >
        <h5 id="record-manager-title" className="modal-title d-flex align-items-center gap-2 mb-0">
          {config.title}
          <span className="badge bg-primary-subtle text-ink-primary">{records.length}</span>
        </h5>
      </ModalHeader>

      <div className={`record-manager${showDetailOnPhone ? " is-showing-detail" : ""}`}>
        <aside className="record-manager__list-pane" aria-label={`${config.title} list`}>
          <div className="record-manager__list-tools">
            {permissions.canAdd ? (
              <button
                type="button"
                className="btn btn-soft-primary w-100"
                onClick={() => request({ type: "select", target: "new" })}
              >
                <i className="ri-add-line me-1" aria-hidden="true"></i>
                Add {config.noun}
              </button>
            ) : null}
            {records.length > 6 ? (
              <div className="search-box">
                <input
                  type="search"
                  className="form-control"
                  placeholder={`Find a ${config.noun}`}
                  aria-label={`Find a ${config.noun}`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <i className="ri-search-line search-icon" aria-hidden="true"></i>
              </div>
            ) : null}
          </div>

          <ul className="record-manager__list">
            {selection === "new" ? (
              <li>
                <button type="button" className="record-manager__item is-selected is-draft" aria-current="true">
                  <span className="record-manager__avatar is-primary" aria-hidden="true">
                    <i className="ri-add-line"></i>
                  </span>
                  <span className="record-manager__item-body">
                    <span className="record-manager__item-title">New {config.noun}</span>
                    <span className="record-manager__item-meta">Not saved yet</span>
                  </span>
                </button>
              </li>
            ) : null}
            {visible.map((record: any) => {
              const summary = config.summary(record);
              const isSelected = selection !== "new" && selection?.id === record.id;
              const status = config.status?.(record);
              const dateLine = config.dateLine
                ? config.dateLine(record)
                : record.updatedAt
                ? `Updated ${formatDay(record.updatedAt)}`
                : null;
              return (
                <li key={record.id}>
                  <button
                    type="button"
                    className={`record-manager__item${isSelected ? " is-selected" : ""}`}
                    aria-current={isSelected ? "true" : undefined}
                    onClick={() => request({ type: "select", target: { id: record.id } })}
                  >
                    <Avatar avatar={summary.avatar} />
                    <span className="record-manager__item-body">
                      <span className="record-manager__item-title">{summary.title}</span>
                      {summary.meta ? (
                        <span className="record-manager__item-meta">{summary.meta}</span>
                      ) : null}
                      {dateLine || status ? (
                        <span className="record-manager__item-date">
                          {dateLine ? (
                            <>
                              <i className="ri-time-line me-1" aria-hidden="true"></i>
                              {dateLine}
                            </>
                          ) : null}
                          {status ? (
                            <span className={dateLine ? "ms-2" : ""}>
                              <StatusChip status={status} />
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </span>
                    {isSelected && dirty ? (
                      <span className="record-manager__unsaved" title="Unsaved changes">
                        <span className="visually-hidden">Unsaved changes</span>
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
            {records.length > 0 && visible.length === 0 ? (
              <li className="record-manager__list-empty">
                No {config.noun}s match “{query.trim()}”.
              </li>
            ) : null}
          </ul>

          {footnote ? <p className="record-manager__footnote">{footnote}</p> : null}
        </aside>

        <section className="record-manager__detail-pane" aria-live="polite">
          {pending ? (
            <div className="record-manager__notice is-warning record-manager__guard" role="alert">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
              <span className="me-auto">
                You have unsaved changes to {pendingName}.
              </span>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setPending(null)}>
                Keep editing
              </button>
              <button type="button" className="btn btn-sm btn-warning" onClick={() => apply(pending)}>
                {pending.type === "close" ? "Discard and close" : "Discard and continue"}
              </button>
            </div>
          ) : null}

          {selection ? (
            <RecordDetail
              key={keyOf(selection)}
              config={config}
              owner={owner}
              record={selectedRecord}
              canSave={canSaveSelected}
              canRemove={permissions.canRemove}
              onDirtyChange={onDirtyChange}
              onSaved={async (savedId, created) => {
                if (created && !savedId) {
                  setIdsBeforeAdd(new Set(records.map((record: any) => record.id)));
                }
                await refresh();
                if (created && savedId) setSelection({ id: savedId });
              }}
              onRemoved={async () => {
                const index = records.findIndex((record: any) => record.id === selectedRecord?.id);
                const next = records[index + 1] || records[index - 1] || null;
                setSelection(next ? { id: next.id } : null);
                setShowDetailOnPhone(false);
                await refresh();
              }}
              onCancelNew={() => {
                onDirtyChange(false);
                setSelection(records.length ? { id: records[0].id } : null);
                setShowDetailOnPhone(false);
              }}
              onBack={() => request({ type: "select", target: null })}
            />
          ) : (
            <div className="record-manager__empty">
              <i className="ri-inbox-line" aria-hidden="true"></i>
              <p className="mb-1 fw-semibold">
                {records.length ? `Pick a ${config.noun} to see its details` : `No ${config.noun}s recorded yet`}
              </p>
              <p className="record-manager__provenance mb-3">
                {records.length
                  ? "Each one is saved on its own, so the others keep their dates."
                  : permissions.canAdd
                  ? `Add the first ${config.noun} for ${ownerName || "this profile"}.`
                  : "Nothing has been recorded yet."}
              </p>
              {!records.length && permissions.canAdd ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => request({ type: "select", target: "new" })}
                >
                  <i className="ri-add-line me-1" aria-hidden="true"></i>
                  Add {config.noun}
                </button>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
};

export default RecordManagerModal;
